import type { PlayerUser } from '../../entities/user/types';
import { chatwootContactName } from './chatwootIdentity';
import type { Language } from '../../shared/i18n/messages';

const CHATWOOT_BASE_URL = 'https://support.vgmable.baby';
const CHATWOOT_WEBSITE_TOKEN = 'PaSd9dyHvdvuBSNfrkNcXtbH';
const CHATWOOT_READY_TIMEOUT_MS = 8_000;

let readyPromise: Promise<void> | null = null;
let identifiedAs = '';
let lastIdentifierHash = '';
let lastContactName = '';

type ChatwootApi = {
  setLocale?: (locale: string) => void;
  setUser?: (identifier: string, profile: Record<string, string>) => void;
  setCustomAttributes?: (attributes: Record<string, string | boolean>) => void;
  toggle?: (state?: 'open' | 'close') => void;
  toggleBubbleVisibility?: (state: 'show' | 'hide') => void;
};

declare global {
  interface Window {
    chatwootSettings?: Record<string, unknown>;
    chatwootSDK?: {
      run?: (options: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: ChatwootApi;
  }
}

export async function openSupportChatwoot({
  user,
  language,
  route,
}: {
  user: PlayerUser | null;
  language: Language;
  route: string;
}) {
  await ensureChatwoot(language);
  identifyChatwootUser(user, language, route);
  // Deliberately no toggleBubbleVisibility('show'): Chatwoot's floating
  // launcher is document-level, so once shown it survives SPA navigation and
  // reappears over the lobby, giving support a second entry point competing
  // with the Help tab. The app supplies its own entries (Help tab, feedback
  // detail, wallet order), so the widget is only ever opened programmatically.
  window.$chatwoot?.toggle?.('open');
}

function ensureChatwoot(language: Language) {
  if (window.$chatwoot) {
    window.$chatwoot.setLocale?.(chatwootLocale(language));
    return Promise.resolve();
  }
  if (readyPromise) return readyPromise;

  window.chatwootSettings = {
    ...(window.chatwootSettings || {}),
    locale: chatwootLocale(language),
    position: 'right',
    type: 'standard',
    // The launcher never renders — see openSupportChatwoot for why.
    hideMessageBubble: true,
  };

  readyPromise = new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('CHATWOOT_TIMEOUT')), CHATWOOT_READY_TIMEOUT_MS);
    const ready = () => {
      window.clearTimeout(timer);
      window.$chatwoot?.setLocale?.(chatwootLocale(language));
      window.$chatwoot?.toggleBubbleVisibility?.('hide');
      resolve();
    };
    window.addEventListener('chatwoot:ready', ready, { once: true });

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-sorteia-chatwoot="1"]');
    if (existingScript) return;

    const script = document.createElement('script');
    script.src = `${CHATWOOT_BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.dataset.sorteiaChatwoot = '1';
    script.onload = () => {
      if (!window.chatwootSDK?.run) return;
      window.chatwootSDK.run({
        websiteToken: CHATWOOT_WEBSITE_TOKEN,
        baseUrl: CHATWOOT_BASE_URL,
      });
    };
    script.onerror = () => {
      window.clearTimeout(timer);
      reject(new Error('CHATWOOT_LOAD_FAILED'));
    };
    document.head.appendChild(script);
  });

  return readyPromise;
}

function identifyChatwootUser(user: PlayerUser | null, language: Language, route: string) {
  const api = window.$chatwoot;
  if (!api || !user) return;
  const identifier = safeString(user.chatwoot_identifier || user.chatwootIdentifier || (user.id ? `sorteia:${user.id}` : userDisplayId(user)));
  if (!identifier) return;

  const identifierHash = safeString(user.chatwoot_identifier_hash || user.chatwootIdentifierHash);
  const contactName = chatwootContactName(user);
  if (api.setUser && (identifier !== identifiedAs || identifierHash !== lastIdentifierHash || contactName !== lastContactName)) {
    const profile: Record<string, string> = {
      name: contactName,
    };
    if (identifierHash) profile.identifier_hash = identifierHash;
    if (safeString(user.email)) profile.email = safeString(user.email);
    if (safeString(user.phone_e164 || user.phoneE164 || user.phone)) profile.phone_number = safeString(user.phone_e164 || user.phoneE164 || user.phone);
    api.setUser(identifier, profile);
    identifiedAs = identifier;
    lastIdentifierHash = identifierHash;
    lastContactName = contactName;
  }

  api.setLocale?.(chatwootLocale(language));
  api.setCustomAttributes?.({
    sorteia_user_id: safeString(user.id),
    sorteia_display_id: userDisplayId(user),
    sorteia_country: safeString(user.country_code).toUpperCase(),
    sorteia_username: safeString(user.username || user.name),
    sorteia_vip_level: safeString(user.vip_level || user.vipLevel || user.level),
    sorteia_locale: chatwootLocale(language),
    sorteia_route: route,
    sorteia_logged_in: true,
  });
}

function chatwootLocale(language: Language) {
  if (language === 'pt-BR') return 'pt_BR';
  if (language === 'es') return 'es';
  return 'en';
}

function userDisplayId(user: PlayerUser | null) {
  const raw = safeString(user?.display_id || user?.displayId);
  if (!raw) return '';
  return raw.toUpperCase().startsWith('S') ? raw : `S${raw}`;
}

function safeString(value: unknown) {
  return String(value || '').trim();
}
